/*
  Shared Motion & Interaction Engine: John Lee Hooker Legacy Spirits
  File: assets/jlhls-motion.js  (one-time upload to Shopify: Assets folder)
  Version: 1.0
  Created: 2026-07-15
  Modified: 2026-07-15

  Wire-up: add to theme.liquid, before </body> (defer keeps it non-blocking):
    <script src="{{ 'jlhls-motion.js' | asset_url }}" defer></script>

  WHAT LIVES HERE (previously duplicated per section):
    - Section lifecycle harness: init on page load AND on theme-editor
      re-renders (shopify:section:load/unload), destroy tears everything
      down — timers, observers, listeners — so the editor never leaks.
    - Carousel engine: arrows, dots, autoplay, pointer drag with momentum
      snap, keyboard arrows, shopify:block:select slide-to. Built ON TOP of
      native scrolling (scrollTo on real slide offsets), so touch, trackpad
      and scroll-snap keep working even where this file never loads.
    - Scroll reveal: IntersectionObserver adds .is-inview to .jlhls-reveal.
    - Marquee: clones strip content accessibly, pauses when off-screen.

  SECTION GLUE CONTRACT (what each section's own <script> does):
    window.JLHLSQ = window.JLHLSQ || [];
    window.JLHLSQ.push({ handle: 'homepage-hero', init: fn, destroy: fn });
    // If the engine is already live it runs the registration immediately;
    // otherwise the queue is drained when this file boots. Load order is
    // therefore irrelevant, and a missing engine simply means: static
    // content, native-scroll carousels, no autoplay — never broken markup.

  Changelog:
    1.0 - Initial extraction from the nine homepage sections
*/

(function () {
  'use strict';
  if (window.JLHLS && window.JLHLS.ready) return; // idempotent across duplicate tags

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var registry = {};      // handle -> {init, destroy}
  var JLHLS = window.JLHLS = { ready: false, reduced: REDUCED };

  /* Flag class: reveal CSS only hides content once we know JS is running. */
  document.documentElement.classList.add('jlhls-motion');

  /* ======================================================================
     LIFECYCLE HARNESS
     Sections register once per handle; the harness finds every matching
     [data-section-handle] element and calls init(el)/destroy(el). Per-
     element state lives on el.__jlhlsState so multiple instances of the
     same section on one page never collide.
     ====================================================================== */

  function initEl(reg, el) {
    if (el.__jlhlsState) return;                       // already live
    el.__jlhlsState = { cleanups: [] };
    /* track(): every listener/timer/observer created in init() must be
       registered here so destroy() can reverse it. */
    el.__jlhlsState.track = function (cleanupFn) { el.__jlhlsState.cleanups.push(cleanupFn); };
    if (!REDUCED || reg.allowReducedMotion) reg.init(el, el.__jlhlsState);
    else if (reg.staticInit) reg.staticInit(el, el.__jlhlsState); // minimal non-animated boot
  }

  function destroyEl(reg, el) {
    var state = el.__jlhlsState;
    if (!state) return;
    if (reg.destroy) reg.destroy(el, state);
    state.cleanups.forEach(function (fn) { try { fn(); } catch (e) { /* never block teardown */ } });
    delete el.__jlhlsState;
  }

  function forEachEl(handle, fn) {
    document.querySelectorAll('[data-section-handle="' + handle + '"]').forEach(fn);
  }

  JLHLS.register = function (reg) {
    registry[reg.handle] = reg;
    forEachEl(reg.handle, function (el) { initEl(reg, el); });
  };

  /* Theme editor re-renders a section's DOM on every setting change:
     unload old instance, init the fresh one. */
  document.addEventListener('shopify:section:load', function (e) {
    Object.keys(registry).forEach(function (handle) {
      var el = e.target.querySelector('[data-section-handle="' + handle + '"]');
      if (el) initEl(registry[handle], el);
    });
  });
  document.addEventListener('shopify:section:unload', function (e) {
    Object.keys(registry).forEach(function (handle) {
      var el = e.target.querySelector('[data-section-handle="' + handle + '"]');
      if (el) destroyEl(registry[handle], el);
    });
  });
  /* Editor block click: if the section registered an onBlockSelect (the
     carousel engine wires one automatically), scroll the block into view. */
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.closest('[data-section-handle]');
    if (el && el.__jlhlsState && el.__jlhlsState.onBlockSelect) {
      el.__jlhlsState.onBlockSelect(e.target);
    }
  });

  /* ======================================================================
     SCROLL REVEAL
     One shared IntersectionObserver for the whole page. Sections opt in by
     putting .jlhls-reveal (+ optional --reveal-order) on elements — no JS
     glue needed. Elements reveal once and are then unobserved (cheap).
     ====================================================================== */

  var revealIO = null;
  function observeReveals(rootEl) {
    if (REDUCED) return; // CSS also forces visible under reduced motion
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          revealIO.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.1 });
    }
    (rootEl || document).querySelectorAll('.jlhls-reveal:not(.is-inview)').forEach(function (el) {
      revealIO.observe(el);
    });
  }
  JLHLS.observeReveals = observeReveals;

  /* Re-scan after editor re-renders (new DOM = new reveal targets). */
  document.addEventListener('shopify:section:load', function (e) { observeReveals(e.target); });

  /* ======================================================================
     CAROUSEL ENGINE
     JLHLS.carousel(rootEl, state, opts) -> api {next, prev, goTo, destroy}

     Physics decisions (why it feels clean):
     - The scroller is the browser's own: momentum, touch, trackpad and
       CSS proximity-snap all native. JS only calls scrollTo() with REAL
       slide offsets (slide.offsetLeft), so there is no rounding drift,
       no clones, no transform math to disagree with user scrolling.
     - Pointer drag maps 1:1 to scrollLeft while held (snap disabled via
       .is-dragging), then releases with a velocity fling: the target
       slide is chosen from projected momentum, clamped to ±1 page, and
       scrolled to smoothly. No sudden sips, no skipped-slide jumps.
     - Dots/arrows compute the active index from the nearest slide offset
       on 'scroll' (rAF-throttled), so they stay honest even after a
       user-initiated fling.
     ====================================================================== */

  JLHLS.carousel = function (rootEl, state, opts) {
    opts = opts || {};
    var viewport = rootEl.querySelector('.jlhls-carousel__viewport');
    var track = rootEl.querySelector('.jlhls-carousel__track');
    if (!viewport || !track) return null;

    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return null;

    var dotsWrap = rootEl.querySelector('[data-carousel-dots]');
    var prevBtn = rootEl.querySelector('[data-carousel-prev]');
    var nextBtn = rootEl.querySelector('[data-carousel-next]');
    var autoplayMs = REDUCED ? 0 : (opts.autoplayMs || 0);
    var autoplayTimer = null;
    var activeIndex = 0;
    var scrollRaf = null;

    function maxScroll() { return viewport.scrollWidth - viewport.clientWidth; }

    /* Nearest slide index for a given scroll position (offset-based, exact). */
    function indexForScroll(x) {
      var best = 0, bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].offsetLeft - track.offsetLeft - x);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    }

    function goTo(index, behavior) {
      index = Math.max(0, Math.min(index, slides.length - 1));
      var left = Math.min(slides[index].offsetLeft - track.offsetLeft, maxScroll());
      viewport.scrollTo({ left: left, behavior: REDUCED ? 'auto' : (behavior || 'smooth') });
    }

    function step(direction) {
      var current = indexForScroll(viewport.scrollLeft);
      var atEnd = viewport.scrollLeft >= maxScroll() - 2;
      var atStart = viewport.scrollLeft <= 2;
      if (direction > 0 && atEnd) return goTo(0);              // wrap forward
      if (direction < 0 && atStart) return goTo(slides.length - 1); // wrap back
      goTo(current + direction);
    }

    /* ---- Active-index bookkeeping (drives dots + aria) ---- */
    function onScroll() {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(function () {
        scrollRaf = null;
        var idx = indexForScroll(viewport.scrollLeft);
        if (idx === activeIndex) return;
        activeIndex = idx;
        updateDots();
        if (opts.onChange) opts.onChange(idx);
      });
    }

    /* ---- Dots ---- */
    var dots = [];
    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      dots = slides.map(function (_, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'jlhls-carousel__dot';
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        b.addEventListener('click', function () { goTo(i); restartAutoplay(); });
        dotsWrap.appendChild(b);
        return b;
      });
      updateDots();
    }
    function updateDots() {
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === activeIndex);
        d.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
      });
    }

    /* ---- Pointer drag with momentum fling ---- */
    var drag = null;
    function onPointerDown(e) {
      if (e.pointerType === 'touch') return;          // touch: native scrolling is already ideal
      if (e.button !== 0) return;
      drag = { startX: e.clientX, startScroll: viewport.scrollLeft, lastX: e.clientX, lastT: e.timeStamp, velocity: 0, moved: false };
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture && viewport.setPointerCapture(e.pointerId);
      stopAutoplay();
    }
    function onPointerMove(e) {
      if (!drag) return;
      var dx = e.clientX - drag.startX;
      if (Math.abs(dx) > 4) drag.moved = true;
      /* velocity: px/ms over the last move, lightly smoothed */
      var dt = Math.max(1, e.timeStamp - drag.lastT);
      drag.velocity = 0.8 * drag.velocity + 0.2 * ((e.clientX - drag.lastX) / dt);
      drag.lastX = e.clientX; drag.lastT = e.timeStamp;
      viewport.scrollLeft = drag.startScroll - dx;    // 1:1 hand-tracking
    }
    function onPointerUp(e) {
      if (!drag) return;
      var d = drag; drag = null;
      viewport.classList.remove('is-dragging');
      viewport.releasePointerCapture && e.pointerId !== undefined && viewport.releasePointerCapture(e.pointerId);
      if (d.moved) {
        /* Project ~180ms of remaining momentum, then settle on the nearest
           slide — clamped to one page so a hard fling can't skip content. */
        var projected = viewport.scrollLeft - d.velocity * 180;
        var target = indexForScroll(projected);
        var current = indexForScroll(d.startScroll);
        var perPage = Math.max(1, Math.round(viewport.clientWidth / slides[0].offsetWidth));
        target = Math.max(current - perPage, Math.min(current + perPage, target));
        goTo(target);
        /* Swallow the click that follows a drag so cards don't navigate. */
        var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
        viewport.addEventListener('click', swallow, { capture: true, once: true });
      }
      restartAutoplay();
    }

    /* ---- Autoplay: paused on hover/focus/hidden-tab/off-screen ---- */
    function stopAutoplay() { if (autoplayTimer) { clearInterval(autoplayTimer); autoplayTimer = null; } }
    function startAutoplay() {
      stopAutoplay();
      if (!autoplayMs) return;
      autoplayTimer = setInterval(function () { step(1); }, autoplayMs);
    }
    function restartAutoplay() { if (autoplayMs) startAutoplay(); }

    var visIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { entry.isIntersecting ? startAutoplay() : stopAutoplay(); });
    }, { threshold: 0.15 });
    visIO.observe(rootEl);

    function onVisibility() { document.hidden ? stopAutoplay() : startAutoplay(); }

    /* ---- Keyboard ---- */
    function onKeydown(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); restartAutoplay(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); restartAutoplay(); }
    }

    /* ---- Wire everything, registering teardown for each binding ---- */
    function on(target, type, fn, opts2) {
      target.addEventListener(type, fn, opts2);
      state.track(function () { target.removeEventListener(type, fn, opts2); });
    }
    on(viewport, 'scroll', onScroll, { passive: true });
    on(viewport, 'pointerdown', onPointerDown);
    on(viewport, 'pointermove', onPointerMove);
    on(viewport, 'pointerup', onPointerUp);
    on(viewport, 'pointercancel', onPointerUp);
    on(viewport, 'keydown', onKeydown);
    on(rootEl, 'pointerenter', stopAutoplay);
    on(rootEl, 'pointerleave', restartAutoplay);
    on(rootEl, 'focusin', stopAutoplay);
    on(rootEl, 'focusout', restartAutoplay);
    on(document, 'visibilitychange', onVisibility);
    if (prevBtn) on(prevBtn, 'click', function () { step(-1); restartAutoplay(); });
    if (nextBtn) on(nextBtn, 'click', function () { step(1); restartAutoplay(); });

    state.track(stopAutoplay);
    state.track(function () { visIO.disconnect(); });
    state.track(function () { if (scrollRaf) cancelAnimationFrame(scrollRaf); });

    /* Editor: clicking a block in the sidebar slides the carousel to it. */
    state.onBlockSelect = function (blockEl) {
      var slide = blockEl.closest('.jlhls-carousel__slide');
      if (slide) { var i = slides.indexOf(slide); if (i >= 0) goTo(i); }
    };

    buildDots();
    startAutoplay();
    return { next: function () { step(1); }, prev: function () { step(-1); }, goTo: goTo };
  };

  /* ======================================================================
     MARQUEE ENGINE
     Duplicates the strip once for the seamless -50% loop. Clones are
     hidden from AT and untabbable. Pauses when the strip leaves the
     viewport (saves main-thread + battery).
     ====================================================================== */

  JLHLS.marquee = function (rootEl, state) {
    var track = rootEl.querySelector('.jlhls-marquee__track');
    if (!track || track.dataset.cloned === 'true') return;
    if (REDUCED) return;                                // CSS shows a scrollable strip instead
    var originals = Array.prototype.slice.call(track.children);
    originals.forEach(function (node) {
      var clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex]').forEach(function (f) { f.setAttribute('tabindex', '-1'); });
      track.appendChild(clone);
    });
    track.dataset.cloned = 'true';

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { rootEl.classList.toggle('is-offscreen', !entry.isIntersecting); });
    }, { threshold: 0 });
    io.observe(rootEl);
    state.track(function () { io.disconnect(); });
  };

  /* ======================================================================
     BOOT — drain the registration queue, then mark ready. Sections that
     load after us call JLHLSQ.push, which we rebind to run immediately.
     ====================================================================== */

  function boot() {
    observeReveals(document);
    var q = window.JLHLSQ = window.JLHLSQ || [];
    q.forEach(function (reg) { JLHLS.register(reg); });
    q.length = 0;
    q.push = function (reg) { JLHLS.register(reg); };  // future pushes run live
    JLHLS.ready = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Parallax: <el data-jlhls-parallax="0.25"> drifts as it crosses the viewport.
   Positive = slower than scroll (classic depth), negative = counter-drift. */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var items = [], ticking = false;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var item = items.find(function (i) { return i.el === entry.target; });
      if (item) item.visible = entry.isIntersecting;
    });
    frame();
  }, { rootMargin: '25%' });

  function scan(root) {
    (root || document).querySelectorAll('[data-jlhls-parallax]').forEach(function (el) {
      if (el.__jlhlsPlx) return;
      el.__jlhlsPlx = true;
      items.push({ el: el, speed: parseFloat(el.getAttribute('data-jlhls-parallax')) || 0.2, visible: false });
      io.observe(el);
    });
  }

  function frame() {
    ticking = false;
    var vh = window.innerHeight;
    items.forEach(function (item) {
      if (!item.visible) return;
      var box = (item.el.parentElement || item.el).getBoundingClientRect();
      var progress = (box.top + box.height / 2 - vh / 2) / ((vh + box.height) / 2); // -1..1
      item.el.style.transform = 'translate3d(0,' + (-progress * item.speed * 100).toFixed(1) + 'px,0)';
    });
  }

  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(frame); }
  }, { passive: true });
  window.addEventListener('resize', frame);
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  scan(document);
})();
})();
